import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { CheckCircle, Loader2, AlertCircle, Shield, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ---------------------------------------------------------------------------
// Background Verification — the candidate's own view of their BGV.
//
// This is JM's BgvCandidatePanel, minus the upload half. JM let the candidate
// upload the four documents from this panel; the ATS has no candidate upload
// route, so this shows status and the signed certificate only. Wording (the
// four titles, the hints, the closing privacy line) is JM's, verbatim.
//
// The panel renders nothing at all when the candidate has no case, exactly as
// JM does — a candidate who has never been through BGV should not be told
// about it.
// ---------------------------------------------------------------------------
const BGV_STATUS_LABEL = {
  pending: 'Pending',
  in_progress: 'Under review',
  clear: 'Cleared',
  discrepancy: 'Needs re-upload',
  unable_to_verify: 'Needs re-upload',
};
const BGV_STATUS_CLASS = {
  pending: 'bg-slate-100 text-slate-700',
  in_progress: 'bg-blue-100 text-blue-700',
  clear: 'bg-green-100 text-green-700',
  discrepancy: 'bg-red-100 text-red-700',
  unable_to_verify: 'bg-amber-100 text-amber-700',
};

export default function VerificationTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let alive = true;
    const token = localStorage.getItem('candidate_token');
    fetch(`${API_URL}/api/candidate/bgv`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (res) => {
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Could not load your verification');
        return res.json();
      })
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setError(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const download = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('candidate_token');
      const res = await fetch(`${API_URL}/api/candidate/bgv/certificate`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Report not available');
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <Card><CardContent className="py-10 flex items-center justify-center gap-2 text-sm text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading verification...
      </CardContent></Card>
    );
  }
  if (error) {
    return (
      <Card><CardContent className="py-8 text-sm text-red-600 flex items-center gap-2">
        <AlertCircle className="w-4 h-4" /> {error}
      </CardContent></Card>
    );
  }
  if (!data || !data.case) {
    return (
      <Card><CardContent className="py-10 text-center text-sm text-slate-500">
        No background verification is in progress for you right now.
      </CardContent></Card>
    );
  }

  const c = data.case;
  const byKind = {};
  (data.stages || []).forEach((s) => { byKind[s.kind] = s.status; });
  const cleared = (data.stages || []).filter((s) => s.status === 'clear').length;
  const total = (data.stages || []).length || 4;
  const closed = c.status === 'completed';

  return (
    <Card data-testid="portal-bgv-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-600" /> Background Verification
            </CardTitle>
            <CardDescription>
              {c.client_name ? `${c.client_name} - ` : ''}{c.position || ''}
            </CardDescription>
          </div>
          <Badge variant="outline" className="capitalize">
            {String(c.status || '').replace(/_/g, ' ')}
          </Badge>
        </div>
        <div className="pt-2 text-xs text-slate-500">{cleared} / {total} checks cleared</div>
      </CardHeader>
      <CardContent className="space-y-3">
        {closed && (
          <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-900 flex items-center justify-between gap-2 flex-wrap">
            <span className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Verification is complete. No further uploads needed.
            </span>
            {c.report_ready && (
              <Button size="sm" variant="outline" onClick={download} disabled={downloading}>
                {downloading
                  ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                  : <FileText className="w-3.5 h-3.5 mr-1" />}
                Download report
              </Button>
            )}
          </div>
        )}

        {(data.checklist || []).map((item) => {
          const st = byKind[item.kind] || 'pending';
          return (
            <div key={item.kind} className="rounded-lg border border-slate-200 p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-sm flex items-center gap-2">
                    {st === 'clear' && <CheckCircle className="w-4 h-4 text-green-600" />}
                    {item.title}
                  </div>
                  <div className="text-xs text-slate-500">{item.hint}</div>
                </div>
                <Badge className={BGV_STATUS_CLASS[st] || BGV_STATUS_CLASS.pending}>
                  {BGV_STATUS_LABEL[st] || st}
                </Badge>
              </div>
              {(st === 'discrepancy' || st === 'unable_to_verify') && (
                <div className="mt-2 text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-900">
                  Our team needs a fresh upload for this section. Please contact your recruiter.
                </div>
              )}
            </div>
          );
        })}

        <div className="text-[11px] text-slate-500 pt-2 border-t">
          Your documents are encrypted, seen only by DCS verifiers, and never shared publicly.
        </div>
      </CardContent>
    </Card>
  );
}
